import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useReplicache } from "../replicache/context";
import type { Scene } from "../replicache/keys";
import {
  collectFileIds,
  parseScene,
  serializeScene,
  type BinaryFiles,
  type ExcalAppState,
  type ExcalElement,
  type ExcalidrawApi,
} from "../scenes/excalidrawTypes";
import { fetchSceneFileAsDataURL, uploadSceneFile } from "../scenes/sceneApi";
import { reconcileElements } from "../scenes/reconcile";
import { Modal } from "./Modal";
import { SceneShareModal } from "../scenes/SceneShareModal";
import { SceneCollab } from "../collab/collab";

// @excalidraw/excalidraw is large (~1MB).  Lazy-load it (and its CSS) so the
// heavy bundle is a separate chunk fetched only when a scene is opened — the
// rest of the app's first load is unaffected.  The CSS import path can differ
// by version; this is the v0.18+ path.
const Excalidraw = lazy(async () => {
  const mod = await import("@excalidraw/excalidraw");
  await import("@excalidraw/excalidraw/index.css");
  return { default: mod.Excalidraw };
});

const SAVE_DEBOUNCE_MS = 1000; // Excalidraw's onChange is chatty
const MAX_SCENE_BYTES = 1_900_000; // stay under the server's 2MB Zod cap
// Backoff for fetching an image referenced by a freshly-arrived collab element:
// the peer that drew it may still be PUTting the bytes to MinIO, so the first
// download-url/GET can 404.  Retry a few times before giving up (~7.5s total).
const FILE_FETCH_RETRY_MS = [0, 500, 1000, 2000, 4000];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

// Track the OS colour scheme so the Excalidraw canvas can match the surrounding
// (media-driven) dark mode.  SSR-safe: defaults to light when there's no window.
function usePrefersDark(): boolean {
  const [isDark, setIsDark] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setIsDark(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return isDark;
}

export function ExcalidrawView({
  scene,
  onClose,
  accessToken,
  currentUserId,
}: {
  scene: Scene;
  onClose: () => void;
  accessToken: string | null;
  currentUserId: string;
}) {
  const rep = useReplicache();
  const isDark = usePrefersDark();
  const [name, setName] = useState(scene.name);
  const [tooLarge, setTooLarge] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const isOwner = scene.ownerId === currentUserId;
  // Owners can always edit; recipients start view-only until the collab relay's
  // `hello` confirms they hold an edit share.
  const [canEdit, setCanEdit] = useState(isOwner);

  // "Maximise" the editor by expanding it to fill the viewport with CSS
  // (position: fixed) rather than the native Fullscreen API.  On iOS/iPadOS
  // Safari, requestFullscreen() overlays the browser's own fullscreen chrome —
  // a grey X plus a swipe-down-to-exit gesture — which sit on top of and fight
  // Excalidraw's toolbar and canvas.  A CSS overlay gives the same full-
  // viewport editor on every browser with no native chrome to interfere; our
  // header button is the exit control.  (We don't bind Escape — Excalidraw uses
  // it to deselect / cancel the active tool.)  Excalidraw's own ResizeObserver
  // re-fits the canvas when the surface resizes.
  const [maximized, setMaximized] = useState(false);
  const toggleFullscreen = useCallback(() => setMaximized((m) => !m), []);

  const apiRef = useRef<ExcalidrawApi | null>(null);
  const dirtyRef = useRef(false);
  const sceneIdRef = useRef(scene.id);
  const nameRef = useRef(name);
  const lastElementsRef = useRef<readonly ExcalElement[]>([]);
  const lastAppStateRef = useRef<ExcalAppState>({});
  const saveTimer = useRef<number | null>(null);
  // fileIds we've already uploaded (or are uploading) this session, so a
  // chatty onChange doesn't re-PUT the same image repeatedly.
  const uploadedFileIds = useRef<Set<string>>(new Set());
  // Token read fresh by the collab socket on (re)connect — it rotates ~15min.
  const accessTokenRef = useRef(accessToken);
  const collabRef = useRef<SceneCollab | null>(null);
  // Guards async file loads that may resolve after the view is closed.
  const mountedRef = useRef(true);
  useEffect(() => () => void (mountedRef.current = false), []);

  useEffect(() => {
    nameRef.current = name;
  }, [name]);

  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  // Excalidraw reads initialData once at mount; the key={scene.id} on the
  // component below remounts it on scene switch so this is recomputed fresh.
  const initialData = useMemo(() => {
    const parsed = parseScene(scene.scene);
    return {
      elements: parsed.elements,
      appState: {
        viewBackgroundColor: parsed.appState.viewBackgroundColor ?? "#ffffff",
      },
      scrollToContent: true,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene.id]);

  // Source of truth for what we persist: the live including-deleted set when the
  // editor API is available (so deletion tombstones and any collaborator
  // elements merged in over the relay are included), falling back to the last
  // onChange snapshot before the API is ready / during teardown.
  const elementsForSave = useCallback((): readonly ExcalElement[] => {
    const api = apiRef.current;
    if (api?.getSceneElementsIncludingDeleted) {
      return api.getSceneElementsIncludingDeleted();
    }
    return lastElementsRef.current;
  }, []);

  const flushSave = useCallback(async () => {
    if (!rep || !dirtyRef.current) return;
    const serialized = serializeScene(
      elementsForSave(),
      lastAppStateRef.current,
    );
    if (serialized.length > MAX_SCENE_BYTES) {
      // Don't silently drop the mutation server-side (the 2MB Zod cap would
      // throw inside push's SAVEPOINT and revert the user's work on next
      // pull).  Surface a banner and keep dirty so a later smaller state saves.
      setTooLarge(true);
      return;
    }
    setTooLarge(false);
    const patch: { id: string; scene?: string; name?: string } = { id: scene.id };
    if (serialized !== scene.scene) patch.scene = serialized;
    if (nameRef.current !== scene.name) patch.name = nameRef.current;
    if (patch.scene !== undefined || patch.name !== undefined) {
      await rep.mutate.updateScene(patch);
    }
    dirtyRef.current = false;
  }, [rep, scene.id, scene.scene, scene.name, elementsForSave]);

  const scheduleSave = useCallback(() => {
    if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      void flushSave();
    }, SAVE_DEBOUNCE_MS);
  }, [flushSave]);

  // Upload any image files Excalidraw has that we haven't offloaded yet.
  const uploadNewFiles = useCallback(
    async (files: BinaryFiles) => {
      if (!accessToken || !files) return;
      for (const [fileId, file] of Object.entries(files)) {
        if (uploadedFileIds.current.has(fileId)) continue;
        uploadedFileIds.current.add(fileId); // mark eagerly to dedupe
        try {
          await uploadSceneFile(
            accessToken,
            scene.id,
            fileId,
            file.dataURL,
            file.mimeType,
          );
        } catch (err) {
          uploadedFileIds.current.delete(fileId); // allow a later retry
          console.warn("[scene] image upload failed", err);
        }
      }
    },
    [accessToken, scene.id],
  );

  // Ensure Excalidraw has the bytes for the given image fileIds, fetching any
  // we don't already have (or aren't already fetching) from MinIO and feeding
  // them to addFiles().  Used on mount, when a collaborator's element arrives
  // over the relay (which carries no bytes), and when the row rebases under us.
  // `uploadedFileIds` doubles as the "have it / in-flight" set so concurrent
  // callers don't double-fetch; a permanent failure un-marks the id to retry.
  const ensureFilesLoaded = useCallback(
    (fileIds: readonly string[]) => {
      const token = accessTokenRef.current;
      if (!token) return;
      const want = fileIds.filter(
        (id) => id && !uploadedFileIds.current.has(id),
      );
      if (want.length === 0) return;
      const sid = scene.id;
      for (const id of want) uploadedFileIds.current.add(id); // dedupe in-flight
      void (async () => {
        const loaded: Array<{
          id: string;
          dataURL: string;
          mimeType: string;
          created: number;
        }> = [];
        for (const fid of want) {
          let got: { dataURL: string; mimeType: string } | null = null;
          for (let i = 0; i < FILE_FETCH_RETRY_MS.length; i++) {
            // Scene switched or view closed — abandon.
            if (!mountedRef.current || sceneIdRef.current !== sid) break;
            if (FILE_FETCH_RETRY_MS[i]) await sleep(FILE_FETCH_RETRY_MS[i]!);
            try {
              got = await fetchSceneFileAsDataURL(token, sid, fid);
            } catch {
              got = null; // network err / object not PUT yet — retry
            }
            if (got) break;
          }
          if (got) {
            loaded.push({
              id: fid,
              dataURL: got.dataURL,
              mimeType: got.mimeType,
              created: Date.now(),
            });
          } else {
            uploadedFileIds.current.delete(fid); // permit a later retry
          }
        }
        if (
          loaded.length > 0 &&
          mountedRef.current &&
          apiRef.current &&
          sceneIdRef.current === sid
        ) {
          apiRef.current.addFiles(
            loaded as unknown as Parameters<ExcalidrawApi["addFiles"]>[0],
          );
        }
      })();
    },
    [scene.id],
  );

  const onChange = useCallback(
    (
      elements: readonly ExcalElement[],
      appState: ExcalAppState,
      files: BinaryFiles,
    ) => {
      lastElementsRef.current = elements;
      lastAppStateRef.current = appState;
      dirtyRef.current = true;
      scheduleSave();
      void uploadNewFiles(files);
      // Broadcast to live collaborators.  Prefer the including-deleted set so
      // deletions propagate; fall back to onChange's elements.
      collabRef.current?.broadcastElements(
        apiRef.current?.getSceneElementsIncludingDeleted?.() ?? elements,
      );
    },
    [scheduleSave, uploadNewFiles],
  );

  const onApi = useCallback((api: ExcalidrawApi) => {
    apiRef.current = api;
  }, []);

  const onPointerUpdate = useCallback(
    (payload: { pointer: { x: number; y: number }; button: "up" | "down" }) => {
      collabRef.current?.broadcastPointer(payload.pointer, payload.button);
    },
    [],
  );

  // Live collaboration: connect to the relay for this scene.  The relay's
  // `hello` tells us whether we may edit (drives view-only mode for read-only
  // recipients); remote element deltas + cursors are applied via the API.
  useEffect(() => {
    const collab = new SceneCollab(scene.id, () => accessTokenRef.current, {
      getApi: () => apiRef.current,
      onCanEdit: (ce) => setCanEdit(ce || isOwner),
      onCollaborators: (m) =>
        apiRef.current?.updateScene({
          // Our loose collaborator map → Excalidraw's branded Map type.
          collaborators: m as unknown as Parameters<
            ExcalidrawApi["updateScene"]
          >[0]["collaborators"],
        }),
      // Relayed elements carry only fileId refs — load any image bytes we lack.
      onRemoteElements: (els) => ensureFilesLoaded(collectFileIds(els)),
    });
    collabRef.current = collab;
    collab.start();
    return () => {
      collab.destroy();
      collabRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene.id]);

  // Load image bytes referenced by the stored scene on mount / scene switch.
  // (Live collab deltas and row rebases load their own newly-referenced files
  // via ensureFilesLoaded; the accessToken dep re-runs this once the token
  // arrives during bootstrap.)
  useEffect(() => {
    ensureFilesLoaded(collectFileIds(parseScene(scene.scene).elements));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene.id, accessToken]);

  // Rebase from the row when it changes underneath us and we have no local
  // edits in flight (another device/collaborator updated it).  On scene
  // switch, just reset the dirty flag — the key remount handles content.
  useEffect(() => {
    if (sceneIdRef.current !== scene.id) {
      sceneIdRef.current = scene.id;
      dirtyRef.current = false;
      setName(scene.name);
      setCanEdit(isOwner);
      uploadedFileIds.current = new Set();
      return;
    }
    if (!dirtyRef.current && apiRef.current) {
      const parsed = parseScene(scene.scene);
      // Reconcile the row into the live canvas (same LWW merge the relay uses)
      // rather than replacing it.  A hard replace deleted any element the
      // incoming row happened to lack — the cause of shapes vanishing when a
      // collaborator's full-document save clobbered the row.  Union-by-version
      // keeps both sides; tombstones in the row still propagate real deletes.
      const local =
        apiRef.current.getSceneElementsIncludingDeleted?.() ??
        apiRef.current.getSceneElements();
      const merged = reconcileElements(local, parsed.elements);
      apiRef.current.updateScene({ elements: merged });
      ensureFilesLoaded(collectFileIds(merged));
      if (scene.name !== nameRef.current) setName(scene.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene]);

  // Flush pending changes on unmount.
  useEffect(() => {
    return () => {
      if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
      if (!rep || !dirtyRef.current) return;
      const serialized = serializeScene(
        elementsForSave(),
        lastAppStateRef.current,
      );
      if (serialized.length > MAX_SCENE_BYTES) return;
      void rep.mutate.updateScene({
        id: scene.id,
        scene: serialized,
        name: nameRef.current,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene.id]);

  const handleDelete = async () => {
    if (!rep) return;
    if (!confirm(`Delete drawing "${name || "Untitled"}"? This cannot be undone.`))
      return;
    await rep.mutate.deleteScene({ id: scene.id });
    onClose();
  };

  // Web-embed (embeddable) support.  Excalidraw's default allowlist rejects
  // any URL that isn't one of its 14 built-in domains (youtube, figma, …), so
  // our own /embed/:token task-list links would be blocked.  Allow same-origin
  // URLs (return undefined for everything else so Excalidraw falls back to its
  // built-in allowlist for youtube/figma/etc.).
  const validateEmbeddable = useCallback((url: string): boolean | undefined => {
    try {
      return new URL(url, window.location.origin).origin ===
        window.location.origin
        ? true
        : undefined;
    } catch {
      return undefined;
    }
  }, []);

  // For our own /embed/* URLs, render the iframe ourselves so we can keep it
  // same-origin (allow-same-origin).  Excalidraw strips allow-same-origin for
  // non-allowlisted domains, which turns the frame into an opaque (null)
  // origin — that breaks localStorage access and makes the data fetch fail
  // CORS.  Returning null for any other URL falls back to Excalidraw's own
  // embed rendering (youtube players, etc.).
  const renderEmbeddable = useCallback((element: ExcalElement) => {
    const link = element.link;
    if (!link) return null;
    let u: URL;
    try {
      u = new URL(link, window.location.origin);
    } catch {
      return null;
    }
    if (
      u.origin !== window.location.origin ||
      !u.pathname.startsWith("/embed/")
    ) {
      return null;
    }
    return (
      <iframe
        title="Embedded task list"
        src={u.href}
        className="h-full w-full"
        style={{ border: "none" }}
        sandbox="allow-same-origin allow-scripts allow-popups"
        allow="clipboard-write"
      />
    );
  }, []);

  return (
    <section
      className={`flex flex-col overflow-hidden bg-white dark:bg-slate-900 ${
        maximized ? "fixed inset-0 z-50" : "flex-1"
      }`}
    >
      <header className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-700 px-4 py-2 sm:px-6">
        <button
          type="button"
          onClick={onClose}
          title="Back to Excalidraw"
          aria-label="Back to Excalidraw"
          className="rounded p-1 text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200"
        >
          ←
        </button>
        <span aria-hidden className="text-base">
          🖼️
        </span>
        <input
          value={name}
          onChange={(e) => {
            dirtyRef.current = true;
            setName(e.target.value);
            scheduleSave();
          }}
          placeholder="Untitled drawing"
          className="flex-1 rounded border border-transparent bg-transparent px-1 py-1 text-lg font-semibold tracking-tight focus:border-slate-300 dark:focus:border-slate-600 focus:bg-white dark:focus:bg-slate-900 focus:outline-none"
        />
        {!isOwner && (
          <span className="hidden rounded bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[11px] text-slate-500 dark:text-slate-400 sm:inline">
            shared with you
          </span>
        )}
        <span className="hidden text-[11px] text-slate-400 dark:text-slate-500 sm:inline">
          Auto-saved · offline-synced
        </span>
        <button
          type="button"
          onClick={toggleFullscreen}
          title={maximized ? "Exit full screen" : "Full screen"}
          aria-label={maximized ? "Exit full screen" : "Full screen"}
          className="rounded p-1 text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200"
        >
          {maximized ? (
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M8 3v3a2 2 0 0 1-2 2H3M21 8h-3a2 2 0 0 1-2-2V3M3 16h3a2 2 0 0 1 2 2v3M16 21v-3a2 2 0 0 1 2-2h3" />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
              </svg>
            )}
          </button>
        {isOwner && accessToken && (
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            className="rounded px-2 py-0.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100"
          >
            Share
          </button>
        )}
        {isOwner && (
          <button
            type="button"
            onClick={() => void handleDelete()}
            className="rounded px-2 py-0.5 text-xs font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950 hover:text-rose-800 dark:hover:text-rose-300"
          >
            Delete
          </button>
        )}
      </header>

      {shareOpen && accessToken && (
        <Modal
          title={`Share "${name || "Untitled"}"`}
          onClose={() => setShareOpen(false)}
        >
          <SceneShareModal
            sceneId={scene.id}
            accessToken={accessToken}
            onClose={() => setShareOpen(false)}
          />
        </Modal>
      )}

      {tooLarge && (
        <div className="border-b border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 px-4 py-1.5 text-xs text-amber-800 dark:text-amber-300">
          This drawing is too large to save (over ~2&nbsp;MB). Remove some
          elements or large images — recent changes won't be saved until it's
          back under the limit.
        </div>
      )}

      <div className="relative flex-1 overflow-hidden bg-white dark:bg-slate-900">
        <Suspense
          fallback={
            <div className="flex h-full items-center justify-center text-sm text-slate-400 dark:text-slate-500">
              Loading editor…
            </div>
          }
        >
          {/* key remounts the editor (fresh initialData) when switching scenes */}
          <Excalidraw
            key={scene.id}
            excalidrawAPI={onApi}
            initialData={initialData}
            theme={isDark ? "dark" : "light"}
            onChange={onChange}
            onPointerUpdate={onPointerUpdate}
            viewModeEnabled={!canEdit}
            validateEmbeddable={validateEmbeddable}
            renderEmbeddable={renderEmbeddable}
            isCollaborating
          />
        </Suspense>
      </div>
    </section>
  );
}
